import React, { FC, useContext, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Skeleton, useMediaQuery } from "@mui/material";

import { SongAuthor, SongStack, SongTitle } from "@pages/Catalog/components/components";

import { useAuth } from "@hooks/auth";
import { useIsDesktop } from "@hooks/layout";
import { useCountDownPlayback } from "@hooks/useCountDownPlayback";
import { FiltersType, navigateToCatalogWithQuery } from "@hooks/useFiltersQuery/utils";
import { useResetDownloadModalStep } from "@hooks/useResetDownloadModalStep";
import { useWhitelistingSuspended } from "@hooks/useWhitelistingSuspended";
import { WHITELISTING_PAUSED_HINT, useWhitelistingPausedRedirect } from "@hooks/useWhitelistingPausedRedirect";

import { createFavorites, deleteFavorites } from "@store/actions/favorites/favorites";
import { TTrack } from "@store/actions/track";
import { useAppDispatchUnwrap, useAppSelector } from "@store/hooks";
import { getFavoritesState } from "@store/selectors/getFavorites";

import NewDownloadModal from "@components/DownloadModal";
import FavoriteHeart from "@components/FavoriteHeart";
import LicenseModal from "@components/LicenseModal";
import { PlayerContext, TPlayerContext } from "@layouts/PlayerLayout/utils";
import { HandleOpenDialog } from "@layouts/SecondRootLayout";
import { IFavorites } from "@models/models";
import { ERoutes } from "@utils/constants";
import { getTitleForDownloading } from "@utils/download";
import { formatTimePlayer } from "@utils/formatTimePlayer";
import { makePath, makePathForTrack } from "@utils/makePath";
import { trackSearchClick } from "@utils/analytics";
import { recordMediaEvent } from "@utils/recordMediaEvent";
import { compressPeaks } from "@utils/waveformDisplay";
import styled from "styled-components";
import WaveSurfer from "wavesurfer.js";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { colors } from "@/styles/design-system";
import { refusedWhileViewing } from "@utils/viewAs";

// -- Styled components for new track row --

const PlayBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;

  &:hover {
    border-color: #ffdca8;
    color: #ffdca8;
    background: rgba(255, 220, 168, 0.06);
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
    stroke: none;
  }
`;

const TrackCover = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  cursor: pointer !important;
  flex-shrink: 0;
  transition: box-shadow 0.3s ease;

  &:hover, &:active {
    box-shadow: rgba(0, 0, 0, 0.3) 0px 7px 29px 0px;
    cursor: pointer !important;
  }
`;

const InfoBlock = styled.div`
  min-width: 0;
  line-height: 1.2;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WaveformContainer = styled.div`
  width: 100%;
  height: 36px;
  position: relative;
  cursor: pointer;
`;

const WaveformCanvasEl = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;


const WaveformTooltip = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(75, 55, 39, 0.95);
  border: 1px solid rgba(186, 144, 92, 0.25);
  color: #fbf4ec;
  font-size: 0.8125rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  padding: 4px 10px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  display: none;
  white-space: nowrap;
  /* Wavesurfer's canvas renders with implicit stacking — force the
     hover pill above it so the tooltip stays visible on hover. */
  z-index: 5;
`;

const Badge = styled.span<{ $variant: "free" | "premium" }>`
  font-size: 0.6875rem;
  padding: 4px 10px;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 96px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;

  /* fixed width so the Free pill is always exactly as wide as the Premium pill */
  svg { height: 11px; width: auto; flex-shrink: 0; }

  ${({ $variant }) =>
    $variant === "free"
      ? `background: ${colors.tier.free.bg}; color: ${colors.tier.free.text};`
      : `background: ${colors.tier.premium.bg}; color: ${colors.tier.premium.text};`}
`;

const Duration = styled.span`
  font-size: 0.875rem;
  color: rgba(251, 244, 236, 0.5);
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  margin-right: -10px;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const TrackBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #ac8d75;
  position: relative;
  overflow: hidden;
  z-index: 0;

  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    fill: transparent;
    stroke-width: 1.5;
    transition: fill 0.2s ease, stroke 0.2s ease;
    position: relative;
    z-index: 1;
  }

  /* Solid blue pseudo for action buttons (revealed on hover) */
  &.track-action-btn::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: #9cd3fd;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 0;
  }

`;

const RemoveBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: rgba(251, 244, 236, 0.5);

  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1;
  }

  &:hover {
    background: rgba(228, 122, 106, 0.15);
    color: #e47a6a;
  }
`;

// -- Icons --

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" style={{ transform: "translateX(1px)" }}>
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="6" y="4" width="4" height="16" fill="currentColor" />
    <rect x="14" y="4" width="4" height="16" fill="currentColor" />
  </svg>
);

const RemoveIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const CartIcon = () => (
  <svg viewBox="0 0 24 24" style={{ color: "#9cd3fd" }}>
    <circle cx="9" cy="20.5" r="1.3" fill="none" /><circle cx="17.4" cy="20.5" r="1.3" fill="none" /><path d="M2 3h3l2.4 12.1a1.7 1.7 0 0 0 1.7 1.4h8.5a1.7 1.7 0 0 0 1.7-1.4L21 7.5H6" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" style={{ color: "#9cd3fd" }}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// -- Phone: tap-to-play overlay on the cover --
const CoverWrap = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
`;

const CoverPlay = styled.button<{ $playing?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  background: ${({ $playing }) => ($playing ? "rgba(255, 220, 168, 0.85)" : "transparent")};
  color: ${({ $playing }) => ($playing ? "#5a3d2a" : "#ffffff")};

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
    /* no dark overlay on the thumbnail — a drop-shadow keeps the play icon
       legible on light covers */
    ${({ $playing }) => (!$playing ? "filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7));" : "")}
  }
  &:hover { background: ${({ $playing }) => ($playing ? "rgba(255, 220, 168, 0.9)" : "transparent")}; }
`;

// -- Phone: subtitle row = tier icon · artist --
const SubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const TierIcon = styled.span<{ $free?: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${({ $free }) => ($free ? "#5fcf9f" : "#ffd29b")};
  /* The green free-use (YouTube) glyph reads as sitting slightly high next to
     the artist baseline — nudge it down 1px. The gold diamond is fine. */
  position: relative;
  top: ${({ $free }) => ($free ? "1px" : "0")};

  svg { height: 12px; width: auto; }
`;

const SubDot = styled.span`
  color: rgba(251, 244, 236, 0.4);
  flex-shrink: 0;
`;

const PhoneArtist = styled.span`
  color: #efd0b8;
  font-family: "Raleway", sans-serif;
  font-size: 0.8rem;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  &:hover { text-decoration: underline; text-underline-offset: 2px; }
`;

const YtGlyph = () => (
  <svg viewBox="0 0 24 18" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M23.498 2.686a2.994 2.994 0 0 0-2.112-2.12C19.544 0 12 0 12 0S4.456 0 2.614.566A2.994 2.994 0 0 0 .502 2.686C0 4.57 0 8.5 0 8.5s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 17 12 17 12 17s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 12.43 24 8.5 24 8.5s0-3.93-.502-5.814zM9.545 12.068V4.932L15.818 8.5l-6.273 3.568z" />
  </svg>
);

const DiamondGlyph = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M6 3h12l4 6-10 13L2 9Z" />
  </svg>
);

const shouldShowDownload = (accountType: AccountType, trackTier: "free" | "premium") => {
  switch (accountType) {
    case AccountType.SUBSCRIPTION:
    case AccountType.MUSIC_CREATOR:
      return true;
    case AccountType.FREE:
    default:
      return trackTier === "free";
  }
};



// -- Waveform generator --

// Wavesurfer-based per-row waveform — peaks-only mode (no audio fetch,
// no decode). Light enough for 250 rows. Visual quality is bounded by
// the resolution of the backend peaks data — bumping `extractPeaks`
// resolution in tracks.file-processor.ts is the corresponding backend
// change that gives this the bottom-player look without runtime cost.
const WavesurferRowWaveform: FC<{
  peaks?: number[];
  duration: number;
  isActive: boolean;
  media?: HTMLMediaElement;
}> = ({ peaks, duration, isActive, media }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WaveSurfer | null>(null);

  useEffect(() => {
    if (!containerRef.current || !peaks || !peaks.length) return;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      peaks: [compressPeaks(peaks)],
      duration: duration || 1,
      // Match the bottom player and /song exactly — same gold palette
      // from `hooks/wafeform.ts`. No playhead cursor on catalog rows.
      waveColor: "#c4a98e",
      progressColor: "#f8d6a7",
      cursorColor: "transparent",
      cursorWidth: 0,
      // RMS peaks are soft-compressed (`compressPeaks`) instead of scaled
      // by a fixed multiplier: a fixed 5x clipped every loud or
      // constant-loudness track into a flat "sausage". The curve keeps
      // loud-end variation visible while preserving cross-track loudness —
      // same curve as the canvas fallback below.
      barHeight: 1,
      normalize: false,
      height: 32,
      fillParent: true,
      interact: false,
    });
    wsRef.current = ws;
    return () => {
      ws.destroy();
      wsRef.current = null;
    };
  }, [peaks, duration]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    if (!isActive || !media) {
      ws.seekTo(0);
      return;
    }
    // Reset immediately when this row becomes the active one — the
    // shared media element may still hold the previous track's
    // `currentTime`. If we seek based on that, we briefly draw the new
    // row's progress at the old position (e.g. 1:00 on a 0:30 click)
    // before the audio actually loads and `timeupdate` corrects it.
    ws.seekTo(0);
    const onTimeUpdate = () => {
      if (media.duration) ws.seekTo(media.currentTime / media.duration);
    };
    media.addEventListener("timeupdate", onTimeUpdate);
    return () => media.removeEventListener("timeupdate", onTimeUpdate);
  }, [isActive, media]);

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
};

// Canvas-based waveform per row. Uses peaks data directly (no per-track
// max normalization) so tracks reflect actual loudness — a quiet track
// stays short, a loud track stays tall, and dynamics within a track read
// clearly. A fixed scale brings typical peak values into the visible bar.
const TrackWaveform: FC<{
  songId: string;
  songName?: string;
  isActive: boolean;
  media?: HTMLMediaElement;
  duration: number;
  url?: string;
  peaks?: number[];
  onSeekPlay?: (percent: number) => void;
  style?: React.CSSProperties;
}> = ({
  songId,
  songName: _songName,
  isActive,
  media,
  duration,
  url: _url,
  peaks: songPeaks,
  onSeekPlay,
  style,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const progressRef = useRef(0);
  const peaksRef = useRef<number[]>([]);
  const rafRef = useRef<number>(0);

  // No per-track normalization. Raw absolute peaks preserve cross-track
  // loudness differences. Fallback for missing peaks is a deterministic
  // synth so rows aren't empty.
  useEffect(() => {
    if (songPeaks?.length) {
      peaksRef.current = songPeaks.map(Math.abs);
    } else {
      let hash = 0;
      for (let i = 0; i < songId.length; i++) {
        hash = (hash << 5) - hash + songId.charCodeAt(i);
        hash |= 0;
      }
      const generated: number[] = [];
      for (let i = 0; i < 2000; i++) {
        const r = Math.abs(Math.sin(hash + i * 127.1) * 43758.5453) % 1;
        generated.push(r);
      }
      peaksRef.current = generated;
    }
  }, [songId, songPeaks]);

  const drawWaveform = React.useCallback(
    (played: number) => {
      const canvas = canvasRef.current;
      if (!canvas || !peaksRef.current.length) return;

      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const width = rect.width;
      const height = rect.height;

      if (width === 0 || height === 0) return;

      canvas.width = width * dpr;
      canvas.height = height * dpr;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      const barCount = Math.floor(width);
      const centerY = height / 2;
      const peaks = peaksRef.current;

      // Soft compression curve: 1 - exp(-peak * k) approaches 1
      // asymptotically without ever clipping. Lower k = less amplification
      // = quieter overall, more variation visible at the loud end.
      const K = 2.7;

      for (let i = 0; i < barCount; i++) {
        const peakIndex = Math.floor((i / barCount) * peaks.length);
        const scaledPeak = 1 - Math.exp(-peaks[peakIndex] * K);
        const barHeight = Math.max(1, scaledPeak * height);
        const isPlayed = played > 0 && i / barCount < played;

        ctx.fillStyle = isPlayed ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.08)";
        ctx.fillRect(i, centerY - barHeight / 2, 1, barHeight);
      }
    },
    [songId],
  );

  // Sync progress from external media when this row is the active track
  useEffect(() => {
    if (!isActive || !media) {
      drawWaveform(0);
      return;
    }

    const onTimeUpdate = () => {
      if (media.duration) {
        progressRef.current = media.currentTime / media.duration;
      }
      rafRef.current = requestAnimationFrame(() => drawWaveform(progressRef.current));
    };

    media.addEventListener("timeupdate", onTimeUpdate);
    if (media.duration) {
      progressRef.current = media.currentTime / media.duration;
    }
    drawWaveform(progressRef.current);

    return () => {
      media.removeEventListener("timeupdate", onTimeUpdate);
      cancelAnimationFrame(rafRef.current);
    };
  }, [isActive, media, drawWaveform]);

  // Redraw on resize / mount
  useEffect(() => {
    drawWaveform(isActive ? progressRef.current : 0);
    const observer = new ResizeObserver(() => drawWaveform(isActive ? progressRef.current : 0));
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [drawWaveform, isActive]);

  const tooltipRef = useRef<HTMLDivElement>(null);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;

    if (isActive && media) {
      // Already playing this track — just seek
      media.currentTime = percent * media.duration;
    } else if (onSeekPlay) {
      // Different track — start it at the clicked position
      onSeekPlay(percent);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;

    if (tooltipRef.current && duration) {
      tooltipRef.current.style.display = "block";
      tooltipRef.current.style.left = `${x + 8}px`;
      tooltipRef.current.textContent = formatTime(percent * duration);
    }
  };

  const handleMouseLeave = () => {
    if (tooltipRef.current) tooltipRef.current.style.display = "none";
  };

  return (
    <WaveformContainer style={style} onClick={handleClick} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      {songPeaks && songPeaks.length ? (
        <WavesurferRowWaveform peaks={songPeaks} duration={duration} isActive={isActive} media={media} />
      ) : (
        <WaveformCanvasEl ref={canvasRef} />
      )}
      <WaveformTooltip ref={tooltipRef} />
    </WaveformContainer>
  );
};

// -- Component --

interface IPlayer {
  song: TTrack;
  isCollapsed?: boolean;
  isFullPage?: boolean;
  flexInfo?: boolean;
  isSemiTransparent?: boolean;
  isFavoritesView?: boolean;
  onRemoveFavorite?: (trackId: string) => void;
  /** 0-based rank of this row in a search-result list (for search-click analytics). */
  resultIndex?: number;
  /** Curated-playlist mode: swaps favorite/download for like/hide, hides the tier
   * badge + store modals. Keeps the exact browse row otherwise (cover/waveform/play). */
  curated?: boolean;
  feedback?: "none" | "liked" | "hidden";
  onFeedback?: (state: "none" | "liked" | "hidden") => void;
}

enum EWaveSurferPlayer {
  IsFavorite = "isFavorite",
  OpenModal = "openModal",
  ActiveStep = "activeStep",
  IsLoading = "isLoading",
  IsPlaying = "isPlaying",
  IsCopyright = "isCopyright",
  Volume = "volume",
  OpenLicenseModal = "openLicenseModal",
}

type WaveSurferPlayerAction =
  | {
      type:
        | EWaveSurferPlayer.IsPlaying
        | EWaveSurferPlayer.IsLoading
        | EWaveSurferPlayer.OpenModal
        | EWaveSurferPlayer.IsFavorite
        | EWaveSurferPlayer.IsCopyright
        | EWaveSurferPlayer.OpenLicenseModal;
      payload: boolean;
    }
  | {
      type: EWaveSurferPlayer.ActiveStep | EWaveSurferPlayer.Volume;
      payload: number;
    };

interface WaveSurferPlayerState {
  isFavorite: boolean;
  openModal: boolean;
  activeStep: number;
  isLoading: boolean;
  isPlaying: boolean;
  isCopyright: boolean;
  volume: number;
  openLicenseModal: boolean;
}

const waveSurferPlayerReducer = (state: WaveSurferPlayerState, action: WaveSurferPlayerAction) => {
  const { type, payload } = action;
  switch (type) {
    case EWaveSurferPlayer.IsFavorite:
      return { ...state, isFavorite: payload };
    case EWaveSurferPlayer.OpenModal:
      return { ...state, openModal: payload };
    case EWaveSurferPlayer.ActiveStep:
      return { ...state, activeStep: payload };
    case EWaveSurferPlayer.IsLoading:
      return { ...state, isLoading: payload };
    case EWaveSurferPlayer.IsPlaying:
      return { ...state, isPlaying: payload };
    case EWaveSurferPlayer.IsCopyright:
      return { ...state, isCopyright: payload };
    case EWaveSurferPlayer.Volume:
      return { ...state, volume: payload };
    case EWaveSurferPlayer.OpenLicenseModal:
      return { ...state, openLicenseModal: payload };
    default:
      return state;
  }
};

const MAX_VOLUME = 1;

// Circle + icon sized 1:1 with the /music favorite heart (TrackBtn: 40px, svg 20px/1.5).
const CuratedHideBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: none;
  cursor: pointer;
  color: #ac8d75;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: rgba(156, 211, 253, 0.18);
    color: #9cd3fd;
  }
`;
// Like (heart) — sits left of Hide. Filled gold once liked; hover previews gold.
const CuratedLikeBtn = styled.button<{ $liked?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $liked }) => ($liked ? "rgba(255, 210, 155, 0.16)" : "rgba(255, 255, 255, 0.06)")};
  border: none;
  cursor: pointer;
  color: ${({ $liked }) => ($liked ? "#ffd29b" : "#ac8d75")};
  transition: all 0.2s ease;

  @media (hover: hover) {
    &:hover {
      background: rgba(255, 210, 155, 0.18);
      color: #ffd29b;
    }
  }
`;
// EyeOff (slash) = hide; Eye (no slash) = it's hidden, click to bring back. 1:1 with /claims.
const HideOffIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
);
const ShowEyeIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const Player: FC<IPlayer> = ({ song, isSemiTransparent = true, flexInfo, isFavoritesView, onRemoveFavorite, resultIndex, curated, feedback = "none", onFeedback }) => {
  const { data, setData, setPlay, media } = useContext(PlayerContext) as TPlayerContext;
  const isDesktop = useIsDesktop();
  const { isUserCanPlay } = useCountDownPlayback();
  // Entitlements, not plan identity: an account awaiting reactivation is on
  // the subscription plan but is not paying for it, so it browses and
  // downloads on free terms until it reactivates (ToU 17.4).
  const { entitlementType: accountType } = useAccountType();

  const navigate = useNavigate();
  const dispatch = useAppDispatchUnwrap();
  const containerRef = useRef<HTMLDivElement>(null);

  const favoritesState = useAppSelector(getFavoritesState);
  const trackTier = (song.tier || "premium") as "free" | "premium";
  const isFree = trackTier === "free";
  const isPhone = useMediaQuery("(max-width:599px)");
  const showTier = accountType === AccountType.FREE; // tier indicator only matters for free-plan users
  const artistName = typeof song.composer === "object" ? ((song.composer as any)?.name ?? "") : ((song.composer as string) ?? "");

  const getFavoritesTracks = (favorites: IFavorites[]) => {
    return new Set(favorites.map((obj) => obj.trackId));
  };

  const favoritesTracks = getFavoritesTracks(favoritesState);
  // Heart state via optimistic override: flips instantly on tap, then defers
  // to Redux once the request lands (or on any external change).
  const reduxFavorite = favoritesTracks.has(song._id);
  const [optimisticFavorite, setOptimisticFavorite] = useState<boolean | null>(null);
  const isFavorite = optimisticFavorite ?? reduxFavorite;
  useEffect(() => { setOptimisticFavorite(null); }, [reduxFavorite]);
  const { isLoggedIn, isLoading } = useAuth();
  const handleOpenDialog = useContext(HandleOpenDialog);
  const [wavUpgrade, setWavUpgrade] = useState(false);
  const [state, dispatchReducer] = useReducer(waveSurferPlayerReducer, {
    isFavorite: false,
    isLoading: false,
    activeStep: 1,
    openModal: false,
    isPlaying: false,
    isCopyright: false,
    volume: MAX_VOLUME,
    openLicenseModal: false,
  });

  const handleNext = () => {
    dispatchReducer({ type: EWaveSurferPlayer.ActiveStep, payload: state.activeStep + 1 });
  };

  useResetDownloadModalStep(state, dispatchReducer, EWaveSurferPlayer.ActiveStep);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const licenseSlug = searchParams.get("license");
    if (licenseSlug && licenseSlug === song.slug && !state.openLicenseModal) {
      dispatchReducer({ type: EWaveSurferPlayer.OpenLicenseModal, payload: true });
    }
  }, [searchParams, song.slug]);

  useEffect(() => {
    if (song._id === data?.id) {
      dispatchReducer({ type: EWaveSurferPlayer.IsPlaying, payload: data?.isPlaying });
    }
  }, [data?.isPlaying, song._id, data?.id]);

  useEffect(() => {
    if (song._id !== data?.id) {
      dispatchReducer({ type: EWaveSurferPlayer.IsPlaying, payload: false });
    }
  }, [data?.id, song._id]);


  const handleUserNotLoggedIn = () => handleOpenDialog(false, true, true);

  const pendingSeekRef = useRef<number | null>(null);

  const onPlayClick = () => {
    if (!isUserCanPlay) return handleOpenDialog(false, true, true);
    if (!state.isPlaying) {
      recordMediaEvent(song._id, "track", "play");
      trackSearchClick(song._id, resultIndex); // no-op unless a search is active
    }
    setPlay(!state.isPlaying);
    if (song._id !== data?.id) {
      setData({ id: song._id, isPlaying: !state.isPlaying, slug: song.slug });
    }
  };

  const onSeekPlay = (percent: number) => {
    if (!isUserCanPlay) return handleOpenDialog(false, true, true);
    pendingSeekRef.current = percent;
    setData({ id: song._id, isPlaying: true, slug: song.slug });
    setPlay(true);
  };

  // Apply pending seek once media is playing the new track
  useEffect(() => {
    if (pendingSeekRef.current === null || !media || data?.id !== song._id) return;

    const seekTo = pendingSeekRef.current;

    const applySeek = () => {
      if (pendingSeekRef.current === null) return;
      pendingSeekRef.current = null;
      if (media.duration) {
        media.currentTime = seekTo * media.duration;
      }
    };

    // Try on multiple events — WaveSurfer may reset currentTime after loadedmetadata
    media.addEventListener("playing", applySeek, { once: true });
    // Also try with a small delay as fallback
    const timeout = setTimeout(() => {
      if (pendingSeekRef.current !== null) applySeek();
    }, 500);

    return () => {
      media.removeEventListener("playing", applySeek);
      clearTimeout(timeout);
    };
  }, [media, data?.id, song._id]);

  const trackPath = song ? makePath(ERoutes.Track, song.slug) : "";
  const albumPath = song?.album?.slug ? makePath(ERoutes.Album, song.album.slug) : "";

  const openInNewTab = (e: React.MouseEvent, url: string) => {
    if (e.ctrlKey || e.metaKey) { e.preventDefault(); window.open(url, "_blank"); return true; }
    return false;
  };

  const preventAutoScroll = (e: React.MouseEvent) => {
    if (e.button === 1) e.preventDefault();
  };

  const middleClick = (e: React.MouseEvent, url: string) => {
    if (e.button === 1) { e.preventDefault(); window.open(url, "_blank"); }
  };

  const handleGoToTrack = (e: React.MouseEvent) => {
    if (!song) return;
    trackSearchClick(song._id, resultIndex); // no-op unless a search is active
    if (openInNewTab(e, trackPath)) return;
    navigate(trackPath);
  };

  const handleNavigateComposer = (composerName: string, e: React.MouseEvent) => {
    if (state.isLoading || !song) return;
    const path = navigateToCatalogWithQuery(composerName, FiltersType.COMPOSER);
    const url = typeof path === "string" ? path : `${path.pathname}?${path.search}`;
    if (openInNewTab(e, url)) return;
    navigate(path);
  };

  const handleGoToAlbum = (e: React.MouseEvent) => {
    if (!song || !albumPath) return;
    if (openInNewTab(e, albumPath)) return;
    navigate(albumPath);
  };

  // Catalog always offers Buy for premium tracks (Free users), even if the user
  // already owns a license — they need to be able to buy additional licenses for
  // the same track. Downloads of purchased tracks happen from the orders page.
  const canDownloadTrack = shouldShowDownload(accountType, trackTier);
  const whitelistingSuspended = useWhitelistingSuspended();
  const goFixWhitelisting = useWhitelistingPausedRedirect();

  const handleDownload = () => {
    // Purchase/download freeze (ToU §17.4): a paused account can't acquire NEW
    // tracks from the catalogue (free or premium) or open the buy flow. The
    // button is greyed, and the click routes to the Account Issues modal rather
    // than doing nothing. Owned re-downloads live on /orders.
    if (whitelistingSuspended) return goFixWhitelisting();
    if (canDownloadTrack) {
      // Even free downloads require an account (free / subscription / music
      // creator). A guest tapping download gets the sign-up-to-continue dialog.
      if (!isLoggedIn && !isLoading) return handleUserNotLoggedIn();
      dispatchReducer({ type: EWaveSurferPlayer.OpenModal, payload: !state.openModal });
    } else {
      dispatchReducer({ type: EWaveSurferPlayer.OpenLicenseModal, payload: !state.openLicenseModal });
    }
  };

  const _handleLicensing = () => navigate(makePathForTrack(ERoutes.Licensing, song?._id));

  // SYNCHRONOUS on purpose: flip the heart synchronously + fire the request
  // without awaiting, so React flushes the icon update immediately (an async
  // handler dispatching before an await only updated after a forced re-render).
  const handleAddToFavorite = () => {
    if (!isLoggedIn && !isLoading) return handleUserNotLoggedIn();
    // Optimistic control: it flips before the server answers, so a view has
    // to be turned away here rather than left to show a change that was refused.
    if (refusedWhileViewing()) return;
    const willFavorite = !isFavorite;
    setOptimisticFavorite(willFavorite);
    void dispatch(willFavorite
      ? createFavorites({ trackId: song._id })
      : deleteFavorites({ trackId: song._id }));
  };

  const _handleCopyright = () => {
    dispatchReducer({ type: EWaveSurferPlayer.IsCopyright, payload: true });
    dispatchReducer({ type: EWaveSurferPlayer.OpenModal, payload: !state.openModal });
  };

  const isActive = song._id === data?.id;

  return (
    <>
      <SongStack style={curated ? { paddingRight: 10 } : undefined} $variant={isPhone ? "phone" : isDesktop ? "desktop" : "tablet"} $isActive={isActive} $isSemiTransparent={isSemiTransparent} $flexInfo={flexInfo} ref={containerRef}>
        {/* PLAY BUTTON (tablet/desktop — on phone it lives on the cover) */}
        {!isPhone && (
          <PlayBtn className="play-btn" onClick={onPlayClick}>
            {state.isPlaying ? <PauseIcon /> : <PlayIcon />}
          </PlayBtn>
        )}

        {/* COVER (+ tap-to-play overlay on phone) */}
        {state.isLoading ? (
          <Skeleton width="48px" height="48px" sx={{ borderRadius: "6px" }} />
        ) : isPhone ? (
          <CoverWrap>
            <TrackCover
              alt="album"
              onClick={handleGoToAlbum}
              onMouseDown={preventAutoScroll}
              onMouseUp={(e) => middleClick(e, albumPath)}
              src={song.smallCoverUrl || song.largeCoverUrl}
            />
            <CoverPlay $playing={state.isPlaying} className="play-btn" onClick={onPlayClick}>
              {state.isPlaying ? <PauseIcon /> : <PlayIcon />}
            </CoverPlay>
          </CoverWrap>
        ) : (
          <TrackCover
            alt="album"
            onClick={handleGoToAlbum}
            onMouseDown={preventAutoScroll}
            onMouseUp={(e) => middleClick(e, albumPath)}
            src={song.smallCoverUrl || song.largeCoverUrl}
          />
        )}

        {/* INFO: TITLE + ARTIST */}
        <InfoBlock>
          <SongTitle style={curated ? { fontSize: "1.05rem" } : undefined} onClick={handleGoToTrack} onMouseDown={preventAutoScroll} onMouseUp={(e) => middleClick(e, trackPath)}>
            {state.isLoading ? <Skeleton width="50px" /> : song.name}
          </SongTitle>
          {isPhone ? (
            <SubRow>
              {showTier && <TierIcon $free={isFree}>{isFree ? <YtGlyph /> : <DiamondGlyph />}</TierIcon>}
              {showTier && <SubDot>·</SubDot>}
              <PhoneArtist
                onClick={(e) => handleNavigateComposer(artistName, e)}
                onMouseDown={preventAutoScroll}
                onMouseUp={(e) => {
                  const path = navigateToCatalogWithQuery(artistName, FiltersType.COMPOSER);
                  const url = typeof path === "string" ? path : `${path.pathname}?${path.search}`;
                  middleClick(e, url);
                }}
              >
                {state.isLoading ? <Skeleton width="70px" /> : artistName}
              </PhoneArtist>
            </SubRow>
          ) : (
            <SongAuthor>
              {state.isLoading ? (
                <Skeleton width="70px" />
              ) : (
                <span
                  className="composer"
                  onClick={(e) =>
                    handleNavigateComposer(
                      typeof song.composer === "object" ? (song.composer as any)?.name : song.composer as string, e,
                    )
                  }
                  onMouseDown={preventAutoScroll}
                  onMouseUp={(e) => {
                    const name = typeof song.composer === "object" ? (song.composer as any)?.name : song.composer as string;
                    const path = navigateToCatalogWithQuery(name, FiltersType.COMPOSER);
                    const url = typeof path === "string" ? path : `${path.pathname}?${path.search}`;
                    middleClick(e, url);
                  }}
                >
                  {song.composer
                    ? (typeof song.composer === "object" && (song.composer as any).name ? (song.composer as any).name : String(song.composer))
                    : ""}
                </span>
              )}
            </SongAuthor>
          )}
        </InfoBlock>

        {/* WAVEFORM */}
        {isDesktop && <TrackWaveform songId={song._id} songName={song.name} isActive={isActive} media={data?.id === song._id ? media : undefined} duration={song.length} url={song.streamingFileUrl || song.lowQualityFileUrl} peaks={song.peaks} onSeekPlay={onSeekPlay} style={flexInfo ? { margin: "0 12px" } : undefined} />}

        {/* DURATION (tablet/desktop) */}
        {!isPhone && <Duration>{formatTimePlayer(song.length)}</Duration>}

        {/* TIER BADGE — only shown for free users / guests, who need to
            know which tracks require a license. Subscribers and Music
            Creators have full access regardless of tier, so the pill
            would just be noise. */}
        {!isPhone && showTier && !curated && (
          <Badge $variant={trackTier}>
            {isFree ? <YtGlyph /> : <DiamondGlyph />}
            {isFree ? "Free" : "Premium"}
          </Badge>
        )}

        {/* ACTIONS */}
        <ActionsContainer>
          {curated ? (
            feedback === "hidden" ? (
              <CuratedHideBtn onClick={() => onFeedback?.("none")}>
                <ShowEyeIcon />
              </CuratedHideBtn>
            ) : (
              <>
                {feedback !== "liked" && (
                  <CuratedHideBtn onClick={() => onFeedback?.("hidden")}>
                    <HideOffIcon />
                  </CuratedHideBtn>
                )}
                <CuratedLikeBtn
                  $liked={feedback === "liked"}
                  onClick={() => onFeedback?.(feedback === "liked" ? "none" : "liked")}
                >
                  <FavoriteHeart active={feedback === "liked"} size={17} strokeWidth={1.5} fillColor="#ffd29b" />
                </CuratedLikeBtn>
              </>
            )
          ) : (
            <>
              {!isFavoritesView && (
                <TrackBtn className="track-btn heart-btn" onClick={handleAddToFavorite}>
                  <FavoriteHeart active={isFavorite} size={20} strokeWidth={1.5} />
                </TrackBtn>
              )}

              <TrackBtn
                className="track-btn track-action-btn"
                onClick={handleDownload}
                title={whitelistingSuspended ? WHITELISTING_PAUSED_HINT : undefined}
              >
                {canDownloadTrack ? <DownloadIcon /> : <CartIcon />}
              </TrackBtn>

              {isFavoritesView && onRemoveFavorite && (
                <RemoveBtn onClick={() => onRemoveFavorite(song._id)}>
                  <RemoveIcon />
                </RemoveBtn>
              )}
            </>
          )}
        </ActionsContainer>
      </SongStack>

      {!curated && (<>
      <NewDownloadModal
        open={state.openModal}
        song={song}
        onClose={() => dispatchReducer({ type: EWaveSurferPlayer.OpenModal, payload: !state.openModal })}
        onOpenLicense={() => {
          dispatchReducer({ type: EWaveSurferPlayer.OpenModal, payload: false });
          setWavUpgrade(true);
          setTimeout(() => {
            dispatchReducer({ type: EWaveSurferPlayer.OpenLicenseModal, payload: true });
          }, 300);
        }}
        copyrightModal={state.isCopyright}
        activeStep={state.activeStep}
        handleNext={handleNext}
        composer={getTitleForDownloading(song)}
        videoUrl={song.videoUrl}
        spotifyUrl={song.spotifyUrl}
        cover={song.mediumCoverUrl || song.largeCoverUrl}
      />

      <LicenseModal
        open={state.openLicenseModal}
        onClose={() => { dispatchReducer({ type: EWaveSurferPlayer.OpenLicenseModal, payload: false }); setWavUpgrade(false); }}
        trackName={song.name}
        trackId={song._id}
        trackSlug={song.slug}
        composerName={
          song.composer
            ? typeof song.composer === "string"
              ? song.composer
              : (song.composer as any)?.name || ""
            : ""
        }
        coverUrl={song.smallCoverUrl || song.mediumCoverUrl || song.largeCoverUrl}
        isFreeUse={isFree}
        tier={song.tier || "premium"}
        licenseTier={(song as any).licenseTier}
        wavUpgrade={wavUpgrade}
      />
      </>)}
    </>
  );
};

export default Player;
